import { MenuItem, useToast } from '@chakra-ui/react'
import { FC, useCallback, useMemo, useRef, useState } from 'react'
import CsvDownloader from 'react-csv-downloader'
import { GrDocumentCsv } from 'react-icons/gr'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'

import useParametrizationCurveStore from '@hook/parametrization/use-curve-store'
import useTools from '@hook/shared/use-tools'

import { parseParametrizatioCurveStoreData } from '@helper/grid/parametrization-curve'
import paramsHelper from '@helper/params'
import parametrizationCurves from '@service/parametrization-curves'

import { ILayoutToolsExportAction } from '@base/@types/components/Layout/Tools'

const ActionExcelCurveNewStoreExport: FC<ILayoutToolsExportAction> = ({
  headers,
  // eslint-disable-next-line unused-imports/no-unused-vars
  parser = parseParametrizatioCurveStoreData
}) => {
  const { isDisabled } = useTools()
  const toast = useToast()
  const { params, query, total, dataOriginal, selecteds, currentyDate } =
    useParametrizationCurveStore()
  const [disabled, setDisabled] = useState(false)
  const csvDownload = useRef(null)
  const [data, updateDataNew] = useImmer<any[]>([])

  const columns = useMemo(() => {
    const data: any[] = []

    for (const header of headers!) {
      if (!header.columns) continue

      for (const column of header.columns) {
        if (column.show) {
          data.push({
            id: column.id,
            displayName: `${header.group} - ${column.name}`
          })
        }
      }
    }

    return data
  }, [headers])

  const rows = useMemo(() => {
    const parsedData = parseParametrizatioCurveStoreData(
      selecteds.length > 0
        ? dataOriginal.filter(item => selecteds.includes(item.id))
        : data
    )

    const body = []

    for (const row of parsedData) {
      const item: any = {}

      for (const column of columns) {
        if (column.id === 'parametros-emax') {
          if (row[column.id].includes('.')) {
            item[column.id] = Number(row[column.id].replace('.', ''))
          } else {
            item[column.id] = Number(row[column.id])
          }
        } else {
          item[column.id] = row[column.id]
        }
      }

      body.push(item)
    }

    return body
  }, [columns, data, dataOriginal, selecteds])

  const dataFetch = useCallback(async () => {
    try {
      Swal.fire({
        title: 'Exportar dados!',
        html: 'Aguarde um momento<b></b>.',
        timerProgressBar: true,
        timer: 99999,
        didOpen: () => {
          Swal.showLoading()
        }
      })
      if (selecteds.length > 0) {
        Swal.close()

        csvDownload.current.handleClick()
      } else {
        const paramsToSend = {
          lojaIds: paramsHelper.parse(params.lojasIds),
          produtoIds: paramsHelper.parse(params.produtosIds),
          empresaIds: paramsHelper.parse(params.empresaIds),
          dataInicio: currentyDate.initialDate,
          dataFim: currentyDate.finalDate,
          search: ''
        }
        await parametrizationCurves
          .getParamsCurveStore(query, 0, total, paramsToSend)
          .then(({ data: { data } }) => {
            updateDataNew(data)
            setDisabled(true)
            Swal.close()

            csvDownload.current.handleClick()
            // downloadCsv(rows, columns, 'OTB - Acompanhamento')
          })
          .catch(() => {
            Swal.close()

            setDisabled(false)
          })
      }
    } catch (error: any) {
      const message = 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
    }
  }, [
    selecteds.length,
    params.lojasIds,
    params.produtosIds,
    params.empresaIds,
    currentyDate.initialDate,
    currentyDate.finalDate,
    query,
    total,
    updateDataNew,
    toast
  ])
  return (
    <>
      {disabled ? (
        <>
          <MenuItem
            p="2px 12px"
            fontSize="sm"
            bgColor="white"
            _hover={{ bg: 'gray.400' }}
            isDisabled={isDisabled}
            onClick={dataFetch}
            icon={<GrDocumentCsv />}
          >
            <CsvDownloader
              filename={'IncrementoEstoque-Parametros'}
              columns={columns}
              datas={rows}
              ref={csvDownload}
              separator=";"
              wrapColumnChar='"'
              bom={true}
              text="Exportar para Tabela"
            ></CsvDownloader>
          </MenuItem>
        </>
      ) : (
        <>
          <MenuItem
            p="2px 12px"
            fontSize="sm"
            bgColor="white"
            _hover={{ bg: 'gray.400' }}
            onClick={dataFetch}
            icon={<GrDocumentCsv />}
          >
            <CsvDownloader
              filename={'CurvaNovaloja-Parametros'}
              columns={columns}
              datas={rows}
              ref={csvDownload}
              separator=";"
              wrapColumnChar='"'
              bom={true}
              text="Exportar para Tabela"
            ></CsvDownloader>
          </MenuItem>
        </>
      )}
    </>
  )
}

export default ActionExcelCurveNewStoreExport
